// RequiereOnboardingCompleto.js
//
// Envoltorio de las rutas de aterrizaje (`/`, `/home`, `/turno`): si la cuenta todavía tiene
// preguntas de "Quién eres" sin responder (`/api/user/me/` → `onboarding_faltante`), en vez de
// la ruta se muestra la pantalla de lo que falta (`/perfil/completar`, `CompletarDatos`).
// Cubre R23, R24, R26 y R27 — .claude/specs/1599-onboarding-calificar/
//
// Por qué un envoltorio y NO un `useEffect` como `ReintroGate`: el effect corre DESPUÉS de
// pintar, así que la ruta de destino alcanza a montarse un instante. Con el Turno eso es una
// pestaña que se crea sola y pedidos al backend debajo de la pantalla (lo vio el revisor de
// #1589: "la pestaña que se crea sola debajo del overlay"). El `<Navigate>` se devuelve en el
// MISMO render que decide, así que los hijos (el Turno) ni se montan (R24).
//
// Nunca encima de otro gate (R27, #1534: dos overlays a la vez): con Términos, celular,
// suscripción o pausa visibles se devuelve el hijo, que el gate tapa, y esto se re-evalúa
// cuando `currentUser` cambia (al cerrarse el gate llega un /me nuevo). Tampoco si hay un
// reintro por mostrar: `ReintroGate` lleva a esa cuenta al wizard, donde Quién eres se pregunta.
//
// Si /me no trae la clave, o trae solo claves que la pantalla no sabe preguntar (desconocidas
// o de una pregunta opcional), no hace nada: nadie queda atrapado por un desajuste de versiones
// entre backend y front (R26).
import React, { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useCurrentUser } from "../context/UserContext";
import { hayGateDeAltaActivo } from "../../helpers/turno-lugares-gate";
import { reintroPorMostrar } from "../../helpers/reintro";
import { faltantesConocidos, reingresoConocido } from "../../helpers/onboarding-completo";
import { marcarReingresoEvaluado, reingresoYaEvaluado } from "../../helpers/reingreso-evaluado";

export const RUTA_FALTANTE = "/perfil/completar";

// Pura, para poder probarla sin montar nada.
export const debeMostrarFaltante = (user) =>
  Boolean(user) &&
  faltantesConocidos(user.onboarding_faltante).length > 0 &&
  !hayGateDeAltaActivo(user) &&
  !reintroPorMostrar(user);

// #1738 R22: lo que le falta a una cuenta que ya usa la app (`reingreso_faltante`), con las mismas
// salvaguardas de «nunca encima de otro gate».
export const debeMostrarReingreso = (user) =>
  Boolean(user) &&
  reingresoConocido(user.reingreso_faltante).length > 0 &&
  !hayGateDeAltaActivo(user) &&
  !reintroPorMostrar(user);

const RequiereOnboardingCompleto = ({ children }) => {
  const { currentUser, loading } = useCurrentUser();
  // R24: el reingreso se decide UNA vez por carga de la app. Un refresco de /me en mitad de la
  // sesión (con el Turno abierto) no saca a nadie; se vuelve a evaluar en la próxima carga. El
  // efecto marca la carga como evaluada cuando ya se vio la cuenta sin un gate encima (si había un
  // gate, se espera: al cerrarse llega un /me nuevo y recién ahí es «la entrada»).
  const evaluable = !loading && Boolean(currentUser) && !hayGateDeAltaActivo(currentUser) && !reintroPorMostrar(currentUser);
  useEffect(() => {
    if (evaluable) marcarReingresoEvaluado();
  }, [evaluable]);
  if (loading) return children;
  if (debeMostrarFaltante(currentUser)) return <Navigate to={RUTA_FALTANTE} replace />;
  if (!reingresoYaEvaluado() && debeMostrarReingreso(currentUser)) return <Navigate to={RUTA_FALTANTE} replace />;
  return children;
};

export default RequiereOnboardingCompleto;
